import { Box, Divider, Typography } from '@mui/material';
import { ReactNode, useState } from 'react';
import { BuilderBoxAnalysis } from '../../types/SimilarlySituated/BuilderBoxAnalysis.type';
import { SsbRateMode } from '../../types/enums/SsbRateMode.enum';
import { missingData, outcomeTitle } from '../../utils/ssbResults';
import OutcomeResultChart from './OutcomeResultChart';
import OutcomeResultTable from './OutcomeResultTable';
import SsbSectionLabel from './SsbSectionLabel.tsx';

interface OutcomeResultCardProps {
  analysis: BuilderBoxAnalysis;
  boxName: string;
  children?: ReactNode;
}

const DEFAULT_REFERENCE_GROUP = 'White';

function FilterPill({
  label,
  isSelected,
  size,
  onClick,
}: {
  label: string;
  isSelected: boolean;
  size: 'medium' | 'small';
  onClick: () => void;
}) {
  return (
    <Box
      component='button'
      type='button'
      aria-pressed={isSelected}
      onClick={onClick}
      sx={{
        borderRadius: '24px',
        border: `1px solid ${isSelected ? (size === 'medium' ? '#93C5FD' : '#132B50') : '#CBD5E0'}`,
        backgroundColor: isSelected ? '#DBEAFE' : '#fff',
        color: isSelected ? (size === 'medium' ? '#1D4ED8' : '#1A365D') : '#4A5568',
        fontSize: '12px',
        fontWeight: isSelected ? 600 : 400,
        padding: size === 'medium' ? '6px 16px' : '4px 12px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 0.2s ease-in-out',
        '&:hover': { borderColor: isSelected ? undefined : '#A0AEC0' },
      }}
    >
      {label}
    </Box>
  );
}

function OutcomeResultCard({ analysis, boxName, children }: OutcomeResultCardProps) {
  const comparisons = analysis.analysis_result?.analysis_results ?? [];
  const rateModes = [...new Set(comparisons.map((comparison) => comparison.comparison_mode))];
  const [rateMode, setRateMode] = useState<SsbRateMode | undefined>(rateModes[0]);
  const groups =
    comparisons.find((comparison) => comparison.comparison_mode === rateMode)?.results_by_racial_reference_group ?? [];
  const [referenceGroup, setReferenceGroup] = useState(DEFAULT_REFERENCE_GROUP);

  const group = groups.find((modeGroup) => modeGroup.racial_reference_group === referenceGroup) ?? groups[0] ?? null;
  const missing = group && missingData(group);

  return (
    <Box
      sx={{
        border: '1px solid #CBD5E0',
        borderRadius: '8px',
        backgroundColor: '#fff',
        padding: '24px',
        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>{outcomeTitle(analysis)}</Typography>
          <Typography sx={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
            measured at {analysis.builder_analysis_type.builder_analysis_type_stage.name} · Box: {boxName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
          {rateModes.map((mode) => (
            <FilterPill
              key={mode}
              label={mode}
              size='medium'
              isSelected={mode === rateMode}
              onClick={() => setRateMode(mode)}
            />
          ))}
        </Box>
      </Box>

      {group && rateMode ? (
        <>
          <Box sx={{ marginTop: '32px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                gap: '6px',
                marginBottom: '8px',
              }}
            >
              <Typography sx={{ fontSize: '13px', color: '#718096', marginRight: '4px' }}>Reference:</Typography>
              {groups.map((modeGroup) => (
                <FilterPill
                  key={modeGroup.racial_reference_group}
                  label={modeGroup.racial_reference_group}
                  size='small'
                  isSelected={modeGroup.racial_reference_group === group.racial_reference_group}
                  onClick={() => setReferenceGroup(modeGroup.racial_reference_group)}
                />
              ))}
            </Box>
            <OutcomeResultTable group={group} mode={rateMode} />
          </Box>

          <Box sx={{ marginTop: '32px' }}>
            <SsbSectionLabel sx={{ marginBottom: '12px' }}> Same result, as a chart</SsbSectionLabel>
            <OutcomeResultChart group={group} mode={rateMode} />
          </Box>
        </>
      ) : (
        <Typography sx={{ fontSize: '12px', color: '#718096', marginTop: '20px' }}>
          No results were returned for this outcome.
        </Typography>
      )}

      {missing && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '24px' }}>
          <Typography sx={{ fontSize: '12px', color: '#718096', fontWeight: 500 }}>
            Excluded for missing data: {missing.excluded} ({missing.percentOfBox}% of box) — missing is never counted as
            no.
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#B7791F', fontWeight: 500 }}>
            Pending caveat: unresolved cases count as not-A — rates shown are a floor.
          </Typography>
        </Box>
      )}

      {children && (
        <>
          <Divider sx={{ backgroundColor: '#EDF1F6', marginY: '24px' }} />
          {children}
        </>
      )}
    </Box>
  );
}

export default OutcomeResultCard;
